import type { EventTask } from "@/api/types/event";

/**
 * Date-only helpers for the events feature — T-minus task overdue math and
 * the calendar month grid. Pure functions, unit-tested (eventDate.test.ts).
 *
 * All "YYYY-MM-DD" strings are parsed as LOCAL calendar dates (via explicit
 * y/m/d components), never via `new Date("YYYY-MM-DD")` — that constructor
 * parses as UTC midnight, which silently shifts a day in any negative UTC
 * offset (all of the US) when compared against a local "today". This is the
 * one DST/month-boundary trap the plan calls out explicitly for this phase.
 */
export function parseIsoDate(iso: string): Date {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, (m ?? 1) - 1, d ?? 1);
}

export function toIsoDate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/** A task is overdue only when it's still open and its due date has passed. */
export function isTaskOverdue(
  task: Pick<EventTask, "due_date" | "status">,
  today: Date = new Date(),
): boolean {
  if (task.status !== "open" || !task.due_date) return false;
  return parseIsoDate(task.due_date).getTime() < startOfDay(today).getTime();
}

/** Ascending by due date (nulls last), tie-broken by the template's sort_offset. */
export function sortTasksByDueDate(tasks: EventTask[]): EventTask[] {
  return [...tasks].sort((a, b) => {
    if (a.due_date && b.due_date) {
      const diff = parseIsoDate(a.due_date).getTime() - parseIsoDate(b.due_date).getTime();
      if (diff !== 0) return diff;
    } else if (a.due_date && !b.due_date) {
      return -1;
    } else if (!a.due_date && b.due_date) {
      return 1;
    }
    return a.sort_offset - b.sort_offset;
  });
}

/** Earliest open task — surfaced as the events list's "next open task" chip. */
export function nextOpenTask(tasks: EventTask[] | undefined): EventTask | undefined {
  if (!tasks || tasks.length === 0) return undefined;
  const open = tasks.filter((t) => t.status === "open");
  if (open.length === 0) return undefined;
  return sortTasksByDueDate(open)[0];
}

export interface MonthGridDay {
  date: Date;
  iso: string;
  inMonth: boolean;
  isToday: boolean;
}

/**
 * Builds a Sun-Sat 6-week (42-cell) grid for the given "YYYY-MM" month,
 * including the leading/trailing days from adjacent months needed to fill
 * complete weeks — a plain CSS grid renders this with no calendar library.
 */
export function getMonthGridDays(month: string, today: Date = new Date()): MonthGridDay[] {
  const [y, m] = month.split("-").map(Number);
  const firstOfMonth = new Date(y, (m ?? 1) - 1, 1);
  const gridStart = new Date(firstOfMonth);
  gridStart.setDate(gridStart.getDate() - firstOfMonth.getDay());

  const todayIso = toIsoDate(today);
  const days: MonthGridDay[] = [];
  for (let i = 0; i < 42; i++) {
    const date = new Date(gridStart);
    date.setDate(gridStart.getDate() + i);
    const iso = toIsoDate(date);
    days.push({
      date,
      iso,
      inMonth: date.getMonth() === (m ?? 1) - 1,
      isToday: iso === todayIso,
    });
  }
  return days;
}

/** Shift a "YYYY-MM" month string by `delta` months (handles Dec->Jan wraps). */
export function shiftMonth(month: string, delta: number): string {
  const [y, m] = month.split("-").map(Number);
  const date = new Date(y, (m ?? 1) - 1 + delta, 1);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}

export function currentMonth(today: Date = new Date()): string {
  return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`;
}

export function formatMonthLabel(month: string): string {
  const [y, m] = month.split("-").map(Number);
  return new Date(y, (m ?? 1) - 1, 1).toLocaleDateString(undefined, {
    month: "long",
    year: "numeric",
  });
}

export function formatShortDate(iso: string | null): string {
  if (!iso) return "—";
  return parseIsoDate(iso).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
}
